import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectField } from '@/components/ui/select';
import { StepSection } from '@/components/step-section';
import { ApiError } from '@/lib/api';
import { use_draft } from '@/features/onboarding/hooks';
import { next_module_step_after } from '@/features/onboarding/module-step-router';
import { use_lab_details, use_save_lab_details } from '@/features/lab/hooks';
import {
  LAB_SAMPLE_TYPE_OPTIONS,
  LAB_SERVICE_RADIUS_OPTIONS,
  type LabServiceRadius,
  type LabSampleType,
} from '@/features/lab/types';

type Form = {
  registration_number: string;
  home_collection_available: boolean;
  service_radius: LabServiceRadius | '';
  sample_types: LabSampleType[];
};

const EMPTY: Form = {
  registration_number: '',
  home_collection_available: true,
  service_radius: '',
  sample_types: [],
};

function toggle<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

/** Keys of `Form` whose value is an array — the multi-select pill fields. */
type ArrayKeys<T> = { [K in keyof T]: T[K] extends unknown[] ? K : never }[keyof T];

export function ModuleLabPage() {
  const navigate = useNavigate();
  const draft_q = use_draft();
  const saved_q = use_lab_details({ enabled: !!draft_q.data });
  const save_m = use_save_lab_details();
  const [form, set_form] = useState<Form>(EMPTY);
  const [prefilled, set_prefilled] = useState(false);
  const [error, set_error] = useState<string | null>(null);

  useEffect(() => {
    if (!draft_q.isFetched) return;
    if (!draft_q.data) {
      navigate('/onboarding/start', { replace: true });
      return;
    }
    if (!draft_q.data.address_completed) {
      navigate('/onboarding/address', { replace: true });
      return;
    }
    if (!draft_q.data.module_types.includes('LAB')) {
      navigate(next_module_step_after('LAB', draft_q.data.module_types), { replace: true });
    }
  }, [draft_q.isFetched, draft_q.data, navigate]);

  useEffect(() => {
    if (prefilled) return;
    if (saved_q.data) {
      set_form({
        registration_number: saved_q.data.registration_number ?? '',
        home_collection_available: saved_q.data.home_collection_available,
        service_radius: saved_q.data.service_radius ?? '',
        sample_types: saved_q.data.sample_types,
      });
      set_prefilled(true);
    }
  }, [saved_q.data, prefilled]);

  function update<K extends keyof Form>(key: K, value: Form[K]) {
    set_form((f) => ({ ...f, [key]: value }));
  }

  /**
   * Toggle a pill *inside* the updater. Computing `toggle(form.x, v)` at the call
   * site reads the render closure, so several picks landing in one React batch
   * all start from the same stale array and only the last one survives.
   */
  function toggle_in<K extends ArrayKeys<Form>>(key: K, value: Form[K][number]) {
    set_form((f) => ({ ...f, [key]: toggle(f[key] as Form[K][number][], value) }));
  }

  async function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    set_error(null);

    if (form.sample_types.length < 1) {
      set_error('Pick at least one sample type your lab can process.');
      return;
    }
    if (form.sample_types.length > 8) {
      set_error('At most 8 sample types.');
      return;
    }

    try {
      await save_m.mutateAsync({
        registration_number:
          form.registration_number.trim() === '' ? undefined : form.registration_number.trim(),
        home_collection_available: form.home_collection_available,
        service_radius: form.service_radius === '' ? undefined : form.service_radius,
        sample_types: form.sample_types,
      });
      if (!draft_q.data) return;
      navigate(next_module_step_after('LAB', draft_q.data.module_types));
    } catch (err) {
      set_error(err instanceof ApiError ? err.message : 'Could not save lab details');
    }
  }

  return (
    <form onSubmit={handle_submit}>
      <StepSection
        step_key="module-lab"
        title="Lab diagnostics details"
        description="Your registration, home sample collection, service area, and the sample types you process."
        error={error}
        footer={
          <Button
            type="submit"
            variant="gradient"
            disabled={save_m.isPending}
            data-testid="module-lab-submit"
          >
            {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save & continue
          </Button>
        }
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="registration_number">Registration / licence number</Label>
          <Input
            id="registration_number"
            data-testid="field-lab-registration"
            value={form.registration_number}
            onChange={(e) => update('registration_number', e.target.value)}
            placeholder="e.g. NABL-12345 (optional)"
            maxLength={120}
          />
        </div>

        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card/60 p-4">
          <label
            className="flex items-center gap-2 text-sm"
            data-testid="field-lab-home-collection-label"
          >
            <input
              type="checkbox"
              data-testid="field-lab-home-collection"
              checked={form.home_collection_available}
              onChange={(e) => update('home_collection_available', e.target.checked)}
              className="h-4 w-4 rounded border-input accent-primary"
            />
            We offer home sample collection
          </label>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="service_radius">Collection service radius</Label>
          <SelectField
            id="service_radius"
            data-testid="field-lab-radius"
            value={form.service_radius}
            onValueChange={(v) => update('service_radius', v as LabServiceRadius | '')}
            options={[{ value: '', label: '— Not specified —' }, ...LAB_SERVICE_RADIUS_OPTIONS]}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label>Sample types processed (pick 1–5)</Label>
          <div className="flex flex-wrap gap-2" data-testid="lab-sample-types">
            {LAB_SAMPLE_TYPE_OPTIONS.map((s) => {
              const active = form.sample_types.includes(s.value);
              return (
                <button
                  key={s.value}
                  type="button"
                  data-testid={`lab-sample-type-${s.value}`}
                  data-checked={active}
                  onClick={() => toggle_in('sample_types', s.value)}
                  className={
                    'rounded-full border px-3 py-1.5 text-sm transition-colors ' +
                    (active
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-input bg-card text-foreground hover:bg-muted')
                  }
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      </StepSection>
    </form>
  );
}
